import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DownloadIcon from "assets/images/download.svg";
import ShareIcon from "assets/images/share.svg";
import PrintIcon from "assets/images/print.svg";
// import GlobalImpact from "assets/images/global_impect.png";
import AffectedPrice from "assets/images/affected_price.png";
import CustomTooltip from "component/news/CustomTooltip.jsx";
import CustomizeArticlePanel from "component/news/CustomizeArticlePanel.jsx";
import ShareDialog from "component/news/ShareDialog.jsx";
import useBreakpoint from "utils/useBreakPoint.js";
import NewsDetailPanel from "component/news/NewsDetailPanel.jsx";
import { useParams } from "react-router-dom";
import { ArticleSkeleton } from "component/news/NewsSkeletons.jsx";
import { SkeletonText } from "component/Loading";
import { getUserId } from "utils/auth.js";
import {
  NEWS_PLACEHOLDER_URL,
  getDisplayArticleLatestVersion,
  getDisplayArticleSimilarDetailed,
} from "modules/News/api";
import ReportCard from "component/reports/ReportCard";
import RelatedNewsPanel from "component/news/RelatedNewsPanel";
import { normalizeLanguageCode } from "utils/language";

const GlobalImpactLazy = lazy(() => import("component/news/GlobalImpact"));
const AffectedPricesLazy = lazy(() => import("component/news/AffectedPrices"));

const hasHtmlTags = (text) => /<\s*[a-z][\s\S]*>/i.test(String(text || ""));
const escapeHtml = (text) =>
  String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
const textToParagraphHtml = (text) => {
  const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!normalized) return "—";
  return normalized
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br/>")}</p>`)
    .join("");
};

const NewsView = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const breakpoint = useBreakpoint();
  const { id } = useParams();
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState(null);
  const [globalImpactReady, setGlobalImpactReady] = useState(false);
  const globalImpactRef = useRef(null);
  const [affectedPricesReady, setAffectedPricesReady] = useState(false);
  const affectedPricesRef = useRef(null);

  const isTranslationPendingError = useCallback((err) => {
    const msg = String(err?.message || "");
    return (
      msg.includes("Requested language translation is pending") ||
      msg.includes("\"Requested language translation is pending\"")
    );
  }, []);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const uid = getUserId();
        let json;
        try {
          json = await getDisplayArticleLatestVersion({
            id,
            userId: uid || undefined,
            language: normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || "en"),
            signal: controller.signal,
          });
        } catch (langErr) {
          if (!isTranslationPendingError(langErr)) throw langErr;
          // Fallback behavior: when requested translation is pending, render source text.
          json = await getDisplayArticleLatestVersion({
            id,
            userId: uid || undefined,
            signal: controller.signal,
          });
        }
        setNewsData(json);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [id, i18n.language, i18n.resolvedLanguage, isTranslationPendingError]);
  // Derived fields with safe fallbacks
  const selectedLang = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || "en");
  const preferLocalizedFields = selectedLang !== "de";
  const seoTitle = preferLocalizedFields
    ? (newsData?.seo_title ?? newsData?.title ?? newsData?.llm_output?.seo_title ?? "—")
    : (newsData?.llm_output?.seo_title ?? newsData?.seo_title ?? newsData?.title ?? "—");
  const title = newsData?.title ?? newsData?.llm_output?.seo_title ?? "—";
  const summaryText = newsData?.summary ?? newsData?.llm_output?.summary ?? "—";
  const articleBodyRaw = preferLocalizedFields
    ? (newsData?.body ?? newsData?.llm_output?.article_body ?? "—")
    : (newsData?.llm_output?.article_body ?? newsData?.body ?? "—");
  const articleBody = hasHtmlTags(articleBodyRaw) ? articleBodyRaw : textToParagraphHtml(articleBodyRaw);
  const image_url = newsData?.llm_output?.image_url ?? newsData?.image_url ?? NEWS_PLACEHOLDER_URL;
  const match_rate = newsData?.llm_output?.match_rate ?? newsData?.match_rate ?? 70;
  const sectors = newsData?.llm_output?.sectors ?? newsData?.sectors ?? ["Sonstiges"];
  const reading_time_minutes = newsData?.llm_output?.reading_time_minutes ?? newsData?.reading_time_minutes ?? 3;
  const created_at = newsData?.llm_output?.created_at ?? newsData?.created_at;
  const sources = newsData?.sources ?? newsData?.llm_output?.sources ?? [];
  const source_logos = (Array.isArray(sources) && sources.length > 0)
    ? sources.map((src) => src?.logo_url).filter(Boolean)
    : newsData?.llm_output?.source_logos ?? newsData?.source_logos ?? [];
  const linkCount = newsData?.link_count ?? (
    Array.isArray(sources) && sources.length > 0
      ? sources.length
      : Array.isArray(source_logos)
        ? source_logos.length
        : 0
  );
  const displayArticleId = newsData?.display_article_id ?? newsData?.id ?? id;
  const showSkeleton = loading;

  useEffect(() => {
    setRelatedError(null);
    setRelatedNews([]);
    if (!displayArticleId) {
      setRelatedLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const loadSimilar = async () => {
      setRelatedLoading(true);
      try {
        const uid = getUserId();
        const results = await getDisplayArticleSimilarDetailed({
          id: displayArticleId,
          userId: uid || undefined,
          limit: 4,
          signal: controller.signal,
        });
        if (cancelled) return;
        setRelatedNews(Array.isArray(results) ? results : []);
      } catch (err) {
        if (cancelled || err.name === "AbortError") return;
        setRelatedError(err.message || "Failed to load related news");
      } finally {
        if (!cancelled) {
          setRelatedLoading(false);
        }
      }
    };

    loadSimilar();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [displayArticleId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setGlobalImpactReady(true);
      setAffectedPricesReady(true);
      return;
    }

    setGlobalImpactReady(false);
    setAffectedPricesReady(false);
    const target = globalImpactRef.current;
    const pricesTarget = affectedPricesRef.current;
    let observer;
    let idleHandle;
    let timeoutId;

    const markReady = () => {
      setGlobalImpactReady(true);
      setAffectedPricesReady(true);
    };

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (entry.target === target) setGlobalImpactReady(true);
            if (entry.target === pricesTarget) setAffectedPricesReady(true);
          });
        },
        { rootMargin: "200px 0px" }
      );
      if (target) observer.observe(target);
      if (pricesTarget) observer.observe(pricesTarget);
    } else if ("requestIdleCallback" in window) {
      idleHandle = window.requestIdleCallback(markReady, { timeout: 1000 });
    } else {
      timeoutId = window.setTimeout(markReady, 600);
    }

    return () => {
      if (observer) {
        if (target) observer.unobserve(target);
        if (pricesTarget) observer.unobserve(pricesTarget);
      }
      if (idleHandle && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [displayArticleId]);

  const handleRewriteResult = useCallback(({ title, summary, body }) => {
    setNewsData((prev) => {
      const next = { ...(prev || {}) };
      if (title) next.title = title;
      if (summary) next.summary = summary;
      if (body) next.body = body;
      // also reflect inside llm_output so existing renderers pick it up
      next.llm_output = { ...(prev?.llm_output || {}) };
      if (title) next.llm_output.seo_title = title;
      if (summary) next.llm_output.summary = summary;
      if (body) next.llm_output.article_body = body;
      return next;
    });
  }, []);

  return (
    <div>
      <div className="main__content__box ">
        {error && !loading && (
          <div className="text-danger py-3">Error: {error}</div>
        )}
        {!error && (
          showSkeleton ? (
            <ArticleSkeleton showSidebar={breakpoint === "laptop"} />
          ) : (
            <div className="container-fluid px-0">
              <div className="row g-4">
                <div
              className={`${["tablet", "mobile"].includes(breakpoint) ? "col-md-12" : "col-md-7"
                } col-sm-12 col-xl-8`}
              style={!["tablet", "mobile"].includes(breakpoint) ? { maxWidth: "950px", flex: "0 1 950px" } : undefined}
            >

              {/* First Card */}
              {["tablet", "mobile"].includes(breakpoint) &&
                <NewsDetailPanel
                  progress={match_rate}
                  sectors={sectors}
                  reading_time_minutes={reading_time_minutes}
                  source_logos={source_logos}
                  sources={sources}
                  linkCount={linkCount}
                />
              }

              <div className="main_contennt_box">
                <div>
                  <div className="news_views_banner_image">
                    <div className="image_box">
                      <img
                        src={image_url}
                        alt={t("news.top.imageAlt")}
                        loading="lazy"
                        className="w-100 h-100 object-fit-cover"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="action_box">
                    <span className="fw-light title_box">{t("news.quickAction")}</span>
                    <div className="action_button_box position-relative">
                      <button className="action_btn">
                        <img src={DownloadIcon} alt="" />
                        <span>{t("news.save")}</span>
                      </button>
                      <button className="action_btn" onClick={() => setOpen(!open)}>
                        <img src={ShareIcon} alt="" />
                        <span>{t("news.share")}</span>
                      </button>
                      <button className="action_btn">
                        <img src={PrintIcon} alt="" />
                        <span>{t("news.print")}</span>
                      </button>
                      <ShareDialog open={open} />
                    </div>
                  </div>
                </div>
                <div className="content_box" style={{ paddingLeft: "50px", paddingRight: "50px" }}>
                  <div className="heading_box pb-4 ">
                    <h3 className="fw-semibold text-white text-center display-6 py-4 news_view_title">
                      {title}
                    </h3>
                    <p className="text-white">
                      {seoTitle}
                    </p>
                  </div>
                  <div className="mini_detail_box mb-4">
                    <h4 className="text-white fw-semibold">
                      {t("news.aiSummary")}
                    </h4>
                    <p className="text-white fw-light mb-0">
                      {summaryText}
                    </p>
                  </div>
                  <div className="news_detail_description_box">
                    <div
                      className="text-white fw-light"
                      dangerouslySetInnerHTML={{ __html: articleBody }}
                    />
                  </div>
                  <div className="news_related_media_box pt-5">
                    <div className="row g-4">
                      <div className="col-12">
                        {/* <img src={RelatedNews} alt="" className="img-fluid" /> */}
                        {/* Accordion Section */}
                        <div className="content_wrap pt-5">
                          <div className="reports_overview_tabsBox">
                            <div className="accordion report_overview_accordion" id="accordionExample">

                              {/* Tab 1: Affected Prices */}
                              <div className="accordion-item">
                                <h2 className="accordion-header">
                                  <button
                                    className="accordion-button text-uppercase fs-5"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#affectedPrices"
                                    aria-expanded="true"
                                    aria-controls="affectedPrices"
                                  >
                                    {t("news.affectedPrices")}
                                  </button>
                                </h2>
                                <div id="affectedPrices" className="accordion-collapse collapse show">
                                  <div className="accordion-body" ref={affectedPricesRef}>
                                    {affectedPricesReady ? (
                                      <Suspense fallback={<div className="skeleton-card p-3"><SkeletonText lines={3} lineHeight={12} gap={8} /></div>}>
                                        <AffectedPricesLazy displayArticleId={displayArticleId} />
                                      </Suspense>
                                    ) : (
                                      <div className="skeleton-card p-3">
                                        <SkeletonText lines={3} lineHeight={12} gap={8} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Tab 2: Global Impact */}
                              <div className="accordion-item">
                                <h2 className="accordion-header">
                                  <button
                                    className="accordion-button text-uppercase fs-5"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#globalImpact"
                                    aria-expanded="true"
                                    aria-controls="globalImpact"
                                  >
                                    {t("news.globalImpact")}
                                  </button>
                                </h2>
                                <div id="globalImpact" className="accordion-collapse collapse show">
                                  <div className="accordion-body" ref={globalImpactRef}>
                                    {globalImpactReady ? (
                                      <Suspense fallback={<div className="skeleton-card p-3"><SkeletonText lines={4} lineHeight={12} gap={8} /></div>}>
                                        <GlobalImpactLazy displayArticleId={displayArticleId} />
                                      </Suspense>
                                    ) : (
                                      <div className="skeleton-card p-3">
                                        <SkeletonText lines={4} lineHeight={12} gap={8} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Tab 3: Related News */}
                              <div className="accordion-item">
                                <h2 className="accordion-header">
                                  <button
                                    className="accordion-button text-uppercase fs-5"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#relatedNews"
                                    aria-expanded="true"
                                    aria-controls="relatedNews"
                                  >
                                    {t("news.relatedNews")}
                                  </button>
                                </h2>
                                <div id="relatedNews" className="accordion-collapse collapse show">
                                  <div className="accordion-body">
                                    <RelatedNewsPanel
                                      relatedNews={relatedNews}
                                      relatedLoading={relatedLoading}
                                      relatedError={relatedError}
                                    />
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {breakpoint === "laptop" &&
              <div className="col-sm-12 col-md-5 col-lg-5  col-xl-4" style={!["tablet", "mobile"].includes(breakpoint) ? { maxWidth: "400px", flex: "0 1 400px" } : undefined}>
                <NewsDetailPanel
                  progress={match_rate}
                  sectors={sectors}
                  reading_time_minutes={reading_time_minutes}
                  create_date={created_at}
                  source_logos={source_logos}
                  sources={sources}
                  linkCount={linkCount}
                />
                <CustomizeArticlePanel displayArticleId={id} onRewriteResult={handleRewriteResult} />
              </div>
            }
          </div>
        </div>
          )
        )}
      </div>
    </div>
  );
};

export default NewsView;
